import React, { useEffect, useState, useRef } from 'react';
import { HeadphonesIcon, PlayIcon, PauseIcon, Volume2Icon, BookOpenIcon } from 'lucide-react';
import { motion } from 'framer-motion';
declare global {
  interface Window {
    fbq: any;
  }
}
const AUDIO_URL = 'https://cdn.magicpatterns.com/uploads/7VKYFCTH5aqsAaHgULRbqY/Audio%20Preview_MS.mp4';
export function AudioPreview() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTimeStr, setCurrentTimeStr] = useState('0:00');
  const [durationStr, setDurationStr] = useState('0:00');
  const [isLoaded, setIsLoaded] = useState(false);
  const formatTime = (seconds: number) => {
    const s = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onLoadedMetadata = () => {
      setDurationStr(formatTime(audio.duration));
      setIsLoaded(true);
    };
    const onTimeUpdate = () => {
      if (audio.duration) {
        const percent = audio.currentTime / audio.duration * 100;
        setProgress(percent);
        setCurrentTimeStr(formatTime(audio.currentTime));
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTimeStr('0:00');
    };
    const onCanPlay = () => {
      setIsLoaded(true);
    };
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('canplay', onCanPlay);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('canplay', onCanPlay);
    };
  }, []);
  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch (e) {
        console.error('Playback error:', e);
      }
    }
  };
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    audio.currentTime = percent * audio.duration;
    setProgress(percent * 100);
    setCurrentTimeStr(formatTime(audio.currentTime));
  };
  const handleBuyClick = () => {
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_ids: ['62906407584113'],
        content_type: 'product',
        content_name: 'Minerales de Sangre eBook + Audio',
        value: 9.97,
        currency: 'USD'
      });
    }
  };
  return <div className="w-full bg-dark-900 pt-2 pb-0 px-4" data-id="element-6">
      <div className="max-w-md mx-auto" data-id="element-7">
        {/* Hidden audio element */}
        <audio ref={audioRef} preload="metadata" src={AUDIO_URL} data-id="element-8" />

        {/* Player Card */}
        <div className="bg-[#1a1a1a] rounded-2xl p-5 border border-white/5 shadow-2xl mb-6" data-id="element-9">
          <div className="flex items-center gap-2 mb-6" data-id="element-10">
            <HeadphonesIcon className="w-5 h-5 text-[#facc15]" data-id="element-11" />
            <h3 className="text-white/90 font-bold text-sm tracking-widest uppercase" data-id="element-12">
              ESCUCHA UN FRAGMENTO
            </h3>
          </div>

          <div className="flex items-center gap-4" data-id="element-13">
            <button onClick={togglePlay} className="w-14 h-14 flex-shrink-0 bg-[#facc15] hover:bg-yellow-400 rounded-full flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#facc15] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]" data-id="element-14">
              {isPlaying ? <PauseIcon className="w-6 h-6 text-black fill-current" data-id="element-15" /> : <PlayIcon className="w-6 h-6 text-black fill-current ml-1" data-id="element-16" />}
            </button>

            <div className="flex-1" data-id="element-17">
              <div className="h-2 bg-white/10 rounded-full mb-2 cursor-pointer relative overflow-hidden" onClick={handleSeek} data-id="element-18">
                <div className="absolute top-0 left-0 h-full bg-[#facc15]/60 rounded-full transition-all duration-100 ease-linear" style={{
                width: `${progress}%`
              }} data-id="element-19" />
              </div>
              <div className="flex justify-between items-center text-xs text-white/50 font-medium" data-id="element-20">
                <span data-id="element-21">{currentTimeStr}</span>
                <span data-id="element-22">{durationStr}</span>
              </div>
            </div>

            <Volume2Icon className="w-5 h-5 text-white/50 flex-shrink-0" data-id="element-23" />
          </div>
        </div>

        {/* CTA Button */}
        <motion.a href="#pricing-cta" onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        const target = document.getElementById('pricing-cta');
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} className="block w-full text-center py-4 rounded-sm font-bold text-lg tracking-wide transition-all duration-300 shadow-[0_0_20px_rgba(220,38,38,0.3)] hover:shadow-[0_0_30px_rgba(220,38,38,0.5)] mb-4 cursor-pointer" style={{
        backgroundColor: '#dc2626',
        color: '#ffffff'
      }} data-id="element-24">
          Comprar Ahora - $9.97
        </motion.a>

        {/* Formats */}
        <div className="flex items-center justify-center gap-4 text-sm text-white/60 font-medium" data-id="element-25">
          <div className="flex items-center gap-1.5" data-id="element-26">
            <BookOpenIcon className="w-4 h-4" data-id="element-27" />
            <span data-id="element-28">eBook PDF</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-white/20" data-id="element-29" />
          <div className="flex items-center gap-1.5" data-id="element-30">
            <HeadphonesIcon className="w-4 h-4" data-id="element-31" />
            <span data-id="element-32">Audio MP3</span>
          </div>
        </div>
      </div>
    </div>;
}